/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/prop-types */
import {
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    darken,
    lighten,
    useTheme,
} from "@mui/material"
import React, { useState } from "react"
import ReactApexChart from "react-apexcharts"

import { BUTTON_RADIUS_MULTIPLIER } from "../../themes/defaultThemeParams"
import { applyLegendFlex, getValue } from "../../utils/general/generalUtils"
import { LCT_DISPLAY_NAME_DISPATCHER } from "../../utils/reporting/reportingGeneralUtils"
import { FIXED_CHART_HEIGHT } from "../../webpages/dnoPortal/dnoHomepage"
import { AppCard, AppCardHeader } from "../surfaces/appCard"
import AppPaper from "../surfaces/appPaper"

interface StatusCount {
    [key: string]: number
}

interface DataByTimeFrame {
    [key: string]: StatusCount
}

export interface Data {
    totLctsByDay: DataByTimeFrame
    totLctsByMonth: DataByTimeFrame
    totLctsByYear: DataByTimeFrame
    [key: string]: DataByTimeFrame
}

interface ChartProps {
    title: string
    description: string
    data: Data[]
    onClick: (period: string) => void
}

const TO_LCTS_BY_DAY = "totLctsByDay"
const TO_LCTS_BY_MONTH = "totLctsByMonth"
const TO_LCTS_BY_YEAR = "totLctsByYear"

/**
 * A stacked bar chart component that displays data visually.
 *
 * @component
 * @param {string} title - The title of the chart.
 * @param {string} description - The description of the chart.
 * @param {DayData[]} data - An array of DayData objects representing the data to be displayed on the chart.
 * @param {function} onClick - A function that handles the button click event for different time frames.
 * @returns {JSX.Element} The rendered stacked bar chart component.
 */
const StackedBarChart: React.FC<ChartProps> = ({
    title,
    description,
    data,
    onClick,
}) => {
    const theme = useTheme()
    const colors = [
        darken(theme.palette.primary.main, 0.5),
        theme.palette.primary.main,
        lighten(theme.palette.primary.main, 0.6),
        darken(theme.palette.primary.light, 0.5),
        theme.palette.primary.light,
        lighten(theme.palette.primary.light, 0.6),
        darken(theme.palette.primary.dark, 0.5),
        theme.palette.primary.dark,
        lighten(theme.palette.primary.dark, 0.6),
    ]

    const [selectedButton, setSelectedButton] = useState(TO_LCTS_BY_DAY)

    const handleButtonClick = (value: string): void => {
        setSelectedButton(value)
        onClick(value)
    }

    const createSeries = (
        dataSeries: any
    ): { name: string; data: number[] }[] => {
        if (!dataSeries) return []
        const techTypes: { [key: string]: number[] } = {}

        const timeFrame = Object.keys(dataSeries)

        timeFrame.forEach((specificTimeFrame, i) => {
            Object.entries(dataSeries[specificTimeFrame]).forEach(
                ([techType, count]) => {
                    if (!techTypes[techType]) {
                        techTypes[techType] = Array(timeFrame.length).fill(null)
                    }
                    if (count !== 0) {
                        techTypes[techType][i] = count as number
                    }
                }
            )
        })

        return Object.entries(techTypes).map(([name, dataInSeries]) => ({
            name: getValue(LCT_DISPLAY_NAME_DISPATCHER, name) ?? name,
            data: dataInSeries,
        }))
    }

    const series = createSeries(data)
    const xAxisData = Object.keys(data ?? {})

    return (
        <AppPaper
            sx={{
                width: "100%",
                minHeight: "100%",
                maxHeight: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <AppCard sx={{ padding: 0 }}>
                <AppCardHeader
                    title={
                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            justifyContent="space-between"
                        >
                            {title}
                            <ToggleButtonGroup
                                color="primary"
                                value="Week"
                                size="small"
                            >
                                <ToggleButton
                                    value="Week"
                                    selected={selectedButton === TO_LCTS_BY_DAY}
                                    onClick={() =>
                                        handleButtonClick(TO_LCTS_BY_DAY)
                                    }
                                    sx={{
                                        borderRadius: BUTTON_RADIUS_MULTIPLIER,
                                        textTransform: "none",
                                        width: "100px",
                                    }}
                                >
                                    Week
                                </ToggleButton>
                                <ToggleButton
                                    value="Month"
                                    selected={
                                        selectedButton === TO_LCTS_BY_MONTH
                                    }
                                    onClick={() =>
                                        handleButtonClick(TO_LCTS_BY_MONTH)
                                    }
                                    sx={{
                                        borderRadius: BUTTON_RADIUS_MULTIPLIER,
                                        textTransform: "none",
                                        width: "100px",
                                    }}
                                >
                                    Month
                                </ToggleButton>
                                <ToggleButton
                                    value="Year"
                                    selected={
                                        selectedButton === TO_LCTS_BY_YEAR
                                    }
                                    onClick={() =>
                                        handleButtonClick(TO_LCTS_BY_YEAR)
                                    }
                                    sx={{
                                        borderRadius: BUTTON_RADIUS_MULTIPLIER,
                                        textTransform: "none",
                                        width: "100px",
                                    }}
                                >
                                    Year
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    }
                    subheader={description}
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                    subheaderTypographyProps={{
                        mt: -0.8,
                    }}
                />
            </AppCard>
            {series.length === 0 ? (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 4 }}
                >
                    No data available
                </Typography>
            ) : (
                <ReactApexChart
                    key={selectedButton}
                    height={FIXED_CHART_HEIGHT}
                    options={{
                        chart: {
                            type: "bar",
                            stacked: false,
                            toolbar: {
                                show: false,
                            },
                            events: {
                                mounted: applyLegendFlex,
                                updated: applyLegendFlex,
                            },
                        },
                        legend: {
                            horizontalAlign: "center",
                            itemMargin: {
                                horizontal: 5,
                                vertical: 4,
                            },
                            markers: {
                                offsetX: -2,
                            },
                            labels: {
                                colors: theme.palette.text.secondary,
                            },
                        },
                        plotOptions: {
                            bar: {
                                horizontal: false,
                                columnWidth: "35",
                                borderRadius:
                                    selectedButton === TO_LCTS_BY_YEAR ? 4 : 8,
                                borderRadiusApplication: "end",
                            },
                        },
                        yaxis: {
                            min: 0,
                            labels: {
                                style: {
                                    colors: theme.palette.text.secondary,
                                },
                            },
                        },
                        xaxis: {
                            categories: xAxisData,
                            axisBorder: {
                                offsetY:
                                    selectedButton === TO_LCTS_BY_YEAR ? 2 : 0,
                            },
                            labels: {
                                rotate: 0,
                                style: {
                                    colors: theme.palette.text.secondary,
                                },
                            },
                        },
                        tooltip: {
                            shared: false,
                            intersect: true,
                        },
                        grid: { show: false },
                        stroke: {
                            colors: ["transparent"],
                            width: 5,
                        },
                        colors,
                    }}
                    series={series}
                    type="bar"
                    sx={{ height: "100%", flexGrow: 1 }}
                />
            )}
        </AppPaper>
    )
}

export default StackedBarChart
